// Privacy-safe, first-party funnel analytics for the public marketing site (T-147).
//
// We measure how visitors move through the four public funnel actions —
// newsletter signup, the Farm Snapshot flow, GSA profile views, and share
// actions — WITHOUT third-party trackers, cookies, device fingerprints, or any
// personally identifiable information.
//
// Privacy guarantees (do not weaken these without a privacy review):
//   - First-party only. Events post to OUR API (same proxy the rest of the site
//     uses); no third-party SDK, pixel, or script is ever loaded.
//   - No PII. The transport only accepts an enumerated event name and a small
//     allowlist of NON-identifying enum-ish properties. Emails, names, free
//     text, precise location, and IP-derived data are never sent from here.
//   - No identifiers. We do not set cookies, do not read/write a device or user
//     id, and do not correlate events across visits. There is no session id.
//   - Consent-respecting. We honour the browser Do-Not-Track and Global Privacy
//     Control signals — when either is set, every call is a silent no-op.
//   - Non-blocking. Delivery is best-effort via `navigator.sendBeacon` (with a
//     keepalive `fetch` fallback). All errors are swallowed; analytics can never
//     break, slow, or block a user action.
//
// If the backend collector is not deployed yet, posts simply 404 and are
// ignored — wiring the events now is safe and forward-compatible.

import { apiUrl } from '@/lib/apiBase'

/** The four public funnel actions this ticket instruments. */
export type FunnelAction =
  | 'signup'
  | 'farm_snapshot'
  | 'gsa'
  | 'share'

/**
 * Allowlisted, non-identifying properties. Everything here must be a low-
 * cardinality enum or a coarse count — never user-provided free text, an email,
 * a name, or a precise location. The transport drops anything not listed here.
 */
export interface FunnelProps {
  /** Funnel stage, e.g. 'view' | 'start' | 'submit' | 'success' | 'error'. */
  step?: string
  /** Where the action originated, e.g. 'newsletter_section' | 'gsa_detail'. */
  surface?: string
  /** Share channel for share events: 'linkedin' | 'facebook' | 'copy_link'. */
  channel?: string
  /**
   * Coarse, non-identifying count (e.g. number of GSA interests selected).
   * A bucketed integer only — never an id that could single out a visitor.
   */
  count?: number
}

// Only these property keys are ever transmitted. Defence-in-depth against a
// caller accidentally passing through PII.
const ALLOWED_KEYS: ReadonlyArray<keyof FunnelProps> = ['step', 'surface', 'channel', 'count']

// First-party collector. Uses the same `/api/agro/*` dev proxy / prod routing
// the rest of the marketing site uses (see vite.config.ts). Overridable for
// tests / alternative deployments.
const ENDPOINT =
  (import.meta.env.VITE_ANALYTICS_URL as string | undefined) ?? apiUrl('/api/agro/analytics/funnel')

/**
 * True when the visitor has asked not to be tracked, via either the legacy
 * Do-Not-Track header or the newer Global Privacy Control signal. When this is
 * the case we emit nothing at all.
 */
export function trackingOptedOut(): boolean {
  if (typeof navigator === 'undefined') return false
  const nav = navigator as Navigator & {
    doNotTrack?: string | null
    globalPrivacyControl?: boolean
    msDoNotTrack?: string | null
  }
  const dnt =
    nav.doNotTrack ??
    (typeof window !== 'undefined'
      ? (window as Window & { doNotTrack?: string | null }).doNotTrack
      : undefined) ??
    nav.msDoNotTrack
  if (dnt === '1' || dnt === 'yes') return true
  if (nav.globalPrivacyControl === true) return true
  return false
}

/** Keep only allowlisted, non-identifying property values. */
function sanitize(props?: FunnelProps): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  if (!props) return out
  for (const key of ALLOWED_KEYS) {
    const value = props[key]
    if (value === undefined || value === null) continue
    if (key === 'count') {
      // Coarse integer only.
      const n = Number(value)
      if (Number.isFinite(n)) out.count = Math.trunc(n)
    } else if (typeof value === 'string') {
      // Short, trimmed enum-ish token; hard cap guards against accidental text.
      const v = value.trim().slice(0, 40)
      if (v) out[key] = v
    }
  }
  return out
}

/**
 * Record one funnel event. Privacy-safe, best-effort, and non-blocking — safe
 * to call from any event handler. Never throws.
 */
export function trackFunnel(action: FunnelAction, props?: FunnelProps): void {
  try {
    if (trackingOptedOut()) return

    const body = JSON.stringify({
      action,
      ...sanitize(props),
      // NOTE: we deliberately do NOT include location.pathname. On identifier-
      // bearing routes (e.g. /news/:id, /gsa-data/:slug) the path embeds a
      // record id/slug, which would defeat the no-identifiers guarantee. The
      // allowlisted `surface` already conveys *where* an action happened at a
      // coarse, non-identifying granularity. Do not re-add a raw path here.
      ts: Date.now(),
    })

    // Prefer sendBeacon: fire-and-forget, survives page unload, never blocks.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' })
      if (navigator.sendBeacon(ENDPOINT, blob)) return
    }

    // Fallback for environments without sendBeacon. keepalive lets it complete
    // even if the page is navigating away. Errors are intentionally ignored.
    if (typeof fetch === 'function') {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {})
    }
  } catch {
    // Analytics must never break the app.
  }
}
